import { FunctionComponent, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styles from './ServicesComponent.module.scss';
import { RootState } from 'store';
import {
    //resetServicesThunk,
    saveServiceThunk,
    deleteServicesThunk,
    activateServicesThunk,
    updatePetTypesServicesThunk,
    updateBusinessAreaServicesThunk,
    saveBulkServicesThunk
} from 'actions/services/ServicesActions';
import { ServicesStatus } from 'reducers/services/ServicesState';
import { fetchStaffThunk } from 'actions/staff/StaffActions';
import Service from 'model/Service';
import { useAuth, useMasterData, useMarketplace, useGroomingServices } from 'hooks';
import { MarketplaceStatus } from '@spike/marketplace-action';
import { ServicesTable } from 'components/Services/ServicesTable';
import ServiceComponent from 'components/Service';
import { Option, PetType } from '@spike/model';
import OnboardingTutorial from 'components/OnboardingTutorial';
import { OnboardingSteps } from 'model/OnboardingSteps';
import {
    fetchOnboardingStepsThunk,
    onboardingCloseModal,
    onboardingShowVideo
} from 'actions/onboardingSteps/OnboardingStepsActions';
import { ONBOARDING_VIDEOS } from 'components/OnboardingSteps/videos';
import cloneDeep from 'lodash/cloneDeep';
import { serviceSlides } from './Slides';
import Analytics from 'analytics/client';
import { Events } from 'analytics/events';

interface Props {
    create?: string;
    serviceId?: number;
    onBack?: () => void;
}

export const ServicesComponent: FunctionComponent<Props> = props => {
    const dispatch = useDispatch();
    const auth = useAuth();
    const masterData = useMasterData();
    const marketplace = useMarketplace();

    // Use specialized hook for grooming+dental
    const { services, meta, loading, hasInitialized, isTrulyEmpty, hasEverHadServices, fetchGroomingServices } =
        useGroomingServices();

    const [marketplaceIds, setMarketplaceIds] = useState<Array<number>>(() =>
        auth.marketplaceId ? [auth.marketplaceId] : []
    );

    const marketplaceStatus = useSelector<RootState, MarketplaceStatus>(state => state.marketplace.status);
    const servicesStatus = useSelector<RootState, ServicesStatus>(state => state.services.status);

    const onboardingSteps = useSelector<RootState, OnboardingSteps>(state => state.onboardingSteps.onboardingSteps);
    const marketplaceId = useSelector<RootState, number | undefined>(
        state => state.login.auth.marketplaceId || state.marketplace.marketplace?.id
    );

    const onboardingPageParams = {
        title: 'Your Services',
        buttonLabel: 'Add Service',
        imageWidth: '100%',
        imageHeight: 'auto'
    };

    const { businessHoursDone, teamDone, servicesDone, petTypesDone } = onboardingSteps;

    const [addStaff, setAddStaff] = useState(false);
    const [businessAreas, setBusinessAreas] = useState<Array<Option<string>>>([]);
    const [isEdition, setIsEdition] = useState(props.create === 'create');
    const [petTypes, setPetTypes] = useState<Array<PetType>>([]);
    const [serviceToEdit, setServiceToEdit] = useState<Service | undefined>(undefined);

    const fetchServices = useCallback(
        (
            options: {
                page?: number;
                status?: string;
                search?: string;
                pageSize?: number;
                marketplaceIds?: Array<number>;
            } = {}
        ) => {
            return fetchGroomingServices({
                ...options,
                marketplaceIds: options.marketplaceIds ?? marketplaceIds
            });
        },
        [fetchGroomingServices, marketplaceIds]
    );

    // Initial setup - runs only once on mount
    useEffect(() => {
        // Reset and fetch staff
        //dispatch(resetServicesThunk());
        dispatch(fetchStaffThunk());

        // Fetch grooming+dental services initially
        fetchServices();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- marketplace filter changes are handled by ServicesTable
    }, [fetchGroomingServices]);

    useEffect(() => {
        if (marketplaceStatus === MarketplaceStatus.FetchSuccess) {
            setPetTypes(masterData.petTypes.filter(pt => marketplace.legacy.petTypes.includes(pt.id)));
            setBusinessAreas(marketplace.businessAreas);
        }
    }, [marketplaceStatus]);

    useEffect(() => {
        if (servicesStatus === ServicesStatus.FetchSuccess) {
            if (props.serviceId) {
                let service = [];
                service = services.filter(service => {
                    return service.id === props.serviceId;
                });
                editService(service[0]);
            }
        }

        if (servicesStatus === ServicesStatus.SaveSuccess || servicesStatus === ServicesStatus.SaveBulkSuccess) {
            dispatch(fetchOnboardingStepsThunk());
        }
    }, [servicesStatus]);

    const goToServicesDashboard = () => {
        setIsEdition(false);
        fetchServices();
    };

    const addService = () => {
        setServiceToEdit(undefined);
        setIsEdition(true);
    };

    const addServiceTracked = () => {
        // One-shot CTA Add Service when in onboarding tutorial context
        if (!onboardingSteps.servicesDone) {
            try {
                const key = `event_one_shot_${Events.ADD_SERVICE_CLICKED}_${marketplaceId}`;
                if (!marketplaceId) {
                    Analytics.track(Events.ADD_SERVICE_CLICKED as any, {});
                } else if (localStorage.getItem(key) !== '1') {
                    Analytics.track(Events.ADD_SERVICE_CLICKED as any, { marketplace_id: marketplaceId });
                    localStorage.setItem(key, '1');
                }
            } catch (e) {
                // swallow non-blocking
            }
        }
        addService();
    };

    const editService = (service: Service) => {
        setAddStaff(false);
        setServiceToEdit(cloneDeep(service));
        setIsEdition(true);
    };

    const addStaffToService = (service: Service) => {
        setAddStaff(true);
        setServiceToEdit(cloneDeep(service));
        setIsEdition(true);
    };

    const saveHandler = (service: Service, changedName?: boolean, applyNewPriceToAppointments?: boolean) => {
        console.info(`---------> saveHandler`);
        console.info({ service });

        dispatch(saveBulkServicesThunk(service, changedName, undefined, applyNewPriceToAppointments));
        //dispatch(saveServiceThunk(service, changedName, undefined, undefined, applyNewPriceToAppointments));
        if (businessHoursDone && teamDone && petTypesDone) {
            dispatch(onboardingCloseModal());
        }
    };

    const deleteHandler = (serviceIds: Array<number>) => {
        dispatch(deleteServicesThunk(serviceIds));
    };

    const activateHandler = (serviceIds: Array<number>, active: boolean) => {
        dispatch(activateServicesThunk(serviceIds, active));
    };

    const updatePetTypesHandler = (serviceIds: Array<number>, petTypeIds: Array<string>) => {
        dispatch(updatePetTypesServicesThunk(serviceIds, petTypeIds));
    };

    const updateBusinessAreaHandler = (serviceIds: Array<number>, businessAreaId: string) => {
        dispatch(updateBusinessAreaServicesThunk(serviceIds, businessAreaId));
    };

    const onboarding = (
        <OnboardingTutorial
            titlePage={onboardingPageParams.title}
            addButtonLabel={onboardingPageParams.buttonLabel}
            onPressAdd={addServiceTracked}
            imageWidth={onboardingPageParams.imageWidth}
            imageHeight={onboardingPageParams.imageHeight}
            onPressLinkToVideo={() => dispatch(onboardingShowVideo(ONBOARDING_VIDEOS.ADD_SERVICE))}
            slides={serviceSlides}
        />
    );

    const servicesComponent = !servicesDone ? (
        onboarding
    ) : (
        <ServicesTable
            {...props}
            save={saveHandler}
            businessAreas={businessAreas}
            petTypes={petTypes}
            addService={addService}
            editService={editService}
            addStaff={addStaffToService}
            delete={deleteHandler}
            activate={activateHandler}
            updatePetTypes={updatePetTypesHandler}
            updateBusinessArea={updateBusinessAreaHandler}
            //Control Props of data from useGroomingServices
            services={services}
            meta={meta}
            loading={loading}
            hasInitialized={hasInitialized}
            isTrulyEmpty={isTrulyEmpty}
            hasEverHadServices={hasEverHadServices}
            onFetchServices={fetchServices}
            selectedMarketplaceIds={marketplaceIds}
            onChangeMarketplaceIds={setMarketplaceIds}
        />
    );

    const serviceComponent = (
        <ServiceComponent
            services={services}
            onBack={goToServicesDashboard}
            onSave={saveHandler}
            service={serviceToEdit}
            addStaff={addStaff}
        />
    );
    return <div className={styles.container}>{!isEdition ? servicesComponent : serviceComponent}</div>;
};

export default ServicesComponent;
